import { AuthMigrationError, GOOGLE_CONNECTION, verifyGoogleClaim, requireFreshLogin, requestError } from './googleMigration'

export const selfRegistrationEnabled = () => process.env.VUE_APP_SELF_REGISTRATION_ENABLED === 'true'
const normalizedEmail = value => typeof value === 'string' ? value.trim().toLowerCase() : ''
const normalizedName = value => typeof value === 'string' ? value.trim().replace(/\s+/g, ' ').toLowerCase() : ''
const validEmail = value => /^[^@\s]+@[^@\s]+$/.test(value)

function accountConflict () {
  return new AuthMigrationError('ARC_ACCOUNT_CHANGED', 'ARC could not verify that this account belongs to your Google identity. Contact your ARC administrator.')
}

export function resolveGoogleIdentity ({ user, claims, managementUser, freshLogin }) {
  if (!user || !claims || typeof user.sub !== 'string' || claims.sub !== user.sub || managementUser?.user_id !== user.sub) {
    throw new AuthMigrationError('IDENTITY_MISMATCH', 'Your Google identity could not be verified. Sign in again.', { action: 'google-login' })
  }
  if (managementUser.blocked) throw new AuthMigrationError('ACCOUNT_BLOCKED', 'This account is blocked. Contact your ARC administrator.')
  const identities = managementUser.identities
  const google = Array.isArray(identities) && identities.length === 1 && identities[0]
  if (!google || google.provider !== GOOGLE_CONNECTION || google.connection !== GOOGLE_CONNECTION ||
      typeof google.user_id !== 'string' || !google.user_id || google.user_id.length > 50 ||
      google.user_id.trim() !== google.user_id || user.sub !== `${GOOGLE_CONNECTION}|${google.user_id}`) {
    throw new AuthMigrationError('GOOGLE_IDENTITY_INVALID', 'This account’s identity links need review. Contact your migration administrator.')
  }
  verifyGoogleClaim({ claims, freshLogin }, google.user_id)
  const email = normalizedEmail(claims.email)
  if (!validEmail(email) || claims.email_verified !== true || user.email_verified !== true || managementUser.email_verified !== true ||
      normalizedEmail(user.email) !== email || normalizedEmail(managementUser.email) !== email) {
    throw new AuthMigrationError('GOOGLE_EMAIL_UNVERIFIED', 'Sign in with a verified Google work email before continuing.', { action: 'google-login' })
  }
  return { googleId: google.user_id, email, name: typeof claims.name === 'string' ? claims.name.trim().replace(/\s+/g, ' ') : '' }
}

// The deployed API returns this specific serialization error for a missing user.
// No other 500 (or generic 404) is evidence that creating an account is safe.
export function isMissingGoogleAccount (error) {
  const response = error?.response
  const body = response?.data
  const messages = body?.errors?.view
  return !!(response?.status === 500 && body && Object.keys(body).length === 1 && body.errors &&
    Object.keys(body.errors).length === 1 && Array.isArray(messages) && messages.length === 1 &&
    typeof messages[0] === 'string' && /^1 validation error for UserOut\n {2}Input should be a valid dictionary or object to extract fields from \[type=model_attributes_type, input_value=None, input_type=NoneType\]\n {4}For further information visit https:\/\/errors\.pydantic\.dev\/2\.\d+\/v\/model_attributes_type$/.test(messages[0]))
}

function validateAccount (account, identity) {
  if (!account || !Number.isInteger(account.id) || account.id <= 0 || typeof account.api_key !== 'string' || !account.api_key ||
      account.google_id !== identity.googleId || normalizedEmail(account.email) !== identity.email ||
      !Object.prototype.hasOwnProperty.call(account, 'ms_graph_id')) throw accountConflict()
  if (account.ms_graph_id !== null) {
    throw new AuthMigrationError('MICROSOFT_LINK_REQUIRED', 'This ARC account has an existing Microsoft identity. Complete its migration before signing in.', { action: 'migrate-account' })
  }
  return account
}

export async function lookupGoogleAccount ({ lookupClient, identity, assertCurrent, baselineState = {} }) {
  assertCurrent()
  let response
  try {
    response = await lookupClient.get('users/me', { params: { google_id: identity.googleId } })
  } catch (error) {
    assertCurrent()
    if (isMissingGoogleAccount(error)) {
      if (baselineState.account || baselineState.creation?.id || baselineState.creation?.api_key) throw accountConflict()
      return null
    }
    throw requestError(error)
  }
  assertCurrent()
  return validateAccount(response.data, identity)
}

function pinAccount (account, baselineState) {
  const baseline = { id: account.id, api_key: account.api_key, ms_graph_id: account.ms_graph_id, google_id: account.google_id, email: account.email }
  for (const expected of [baselineState.account, baselineState.creation]) {
    if (expected && Object.keys(expected).some(field => baseline[field] !== expected[field])) throw accountConflict()
  }
  baselineState.account = baseline
}

export async function verifyGoogleAccount ({ account, identity, createUserClient, baselineState, assertCurrent }) {
  validateAccount(account, identity)
  pinAccount(account, baselineState)
  assertCurrent()
  const { data } = await createUserClient(account.api_key).get('users/me')
  assertCurrent()
  validateAccount(data, identity)
  pinAccount(data, baselineState)
  return data
}

const escapeSearch = value => value.replace(/([+\-=&|><!(){}[\]^"~*?:\\/])/g, '\\$1')

function searchFailure () {
  return new AuthMigrationError('REGISTRATION_CHECK_FAILED', 'ARC could not finish checking for an existing account. Please try again.', { retryable: true, action: 'retry' })
}

function maskedCandidate (profile, reason) {
  const [local, domain] = profile.email.split('@')
  const words = profile.name.trim().split(/\s+/)
  return {
    name: words.length > 1 ? `${words[0]} ${words[words.length - 1][0]}.` : `${words[0][0]}…`,
    email: `${local[0]}•••${local.length > 1 ? local.slice(-1) : ''}@${domain}`,
    reason
  }
}

export async function findLegacyMatches ({ managementClient, identity, assertCurrent }) {
  const username = identity.email.split('@')[0]
  const name = normalizedName(identity.name)
  if (!name) throw searchFailure()
  const nameQuery = ` OR name:${name.split(' ').map(escapeSearch).join('*')}`
  const query = `user_id:waad* AND (email:${escapeSearch(username)}@*${nameQuery})`
  const ids = new Set()
  let total
  for (let page = 0; page < 10; page++) {
    assertCurrent()
    let data
    try {
      const response = await managementClient.get('users', {
        params: { q: query, search_engine: 'v3', include_totals: true, per_page: 100, page, fields: 'user_id', include_fields: true }
      })
      data = response.data
    } catch (error) {
      assertCurrent()
      throw searchFailure()
    }
    assertCurrent()
    if (!data || !Array.isArray(data.users) || !Number.isInteger(data.total) || data.total < 0 || data.total >= 1000 ||
        data.start !== page * 100 || (total !== undefined && total !== data.total) ||
        data.users.length !== Math.min(100, data.total - data.start)) throw searchFailure()
    total = data.total
    for (const profile of data.users) {
      if (typeof profile?.user_id !== 'string' || !profile.user_id.startsWith('waad|') || ids.has(profile.user_id)) throw searchFailure()
      ids.add(profile.user_id)
    }
    if (ids.size === total) break
  }
  if (ids.size !== total) throw searchFailure()
  const matches = []
  for (const id of ids) {
    assertCurrent()
    let profile
    try {
      const { data } = await managementClient.get(`users/${encodeURIComponent(id)}`)
      profile = data
    } catch (error) {
      assertCurrent()
      if (error.response?.status === 404) continue // Deleted since the search index was updated.
      throw searchFailure()
    }
    assertCurrent()
    if (profile?.user_id !== id || profile.identities?.[0]?.provider !== 'waad' ||
        !validEmail(profile.email) || typeof profile.name !== 'string' || !profile.name.trim()) throw searchFailure()
    const reason = normalizedEmail(profile.email).split('@')[0] === username
      ? 'Same email username'
      : (name && normalizedName(profile.name) === name ? 'Same full name' : null)
    if (reason) matches.push({ profile, reason })
  }
  matches.sort((a, b) => a.profile.user_id.localeCompare(b.profile.user_id))
  return {
    usernameMatch: matches.some(match => match.reason === 'Same email username'),
    candidates: matches.map(match => maskedCandidate(match.profile, match.reason)),
    // Kept only in memory; binds dismissal to this exact set of matching profiles.
    fingerprint: JSON.stringify(matches.map(({ profile, reason }) => [profile.user_id, normalizedEmail(profile.email), normalizedName(profile.name), reason]))
  }
}

// Organization membership is enforced by the Google OAuth application's audience.
// ARC verifies the authenticated identity without maintaining an email-domain list.
export function assertRegistrationAllowed () {
  if (!selfRegistrationEnabled()) throw new AuthMigrationError('REGISTRATION_DISABLED', 'New account registration is not available in this environment yet.')
}

export async function prepareGoogleRegistration (options) {
  const { identity, context, assertCurrent } = options
  const account = await lookupGoogleAccount(options)
  if (account) return { account: await verifyGoogleAccount({ ...options, account }) }
  assertRegistrationAllowed()
  const matches = await findLegacyMatches(options)
  assertCurrent()
  let needsFreshLogin = false
  try { requireFreshLogin(context, identity.googleId) } catch (error) { needsFreshLogin = true }
  return { identity, matches, needsFreshLogin }
}

function registrationFailure (error) {
  const errors = error?.response?.data?.validation_error?.body_params
  if (error?.response?.status === 400 && Array.isArray(errors) && errors.length === 1 && /^Name .*already taken$/.test(errors[0]?.msg)) {
    const safe = new AuthMigrationError('REGISTRATION_NAME_TAKEN', 'This name is already in use. Add a middle initial or another distinguishing part of your name.', { action: 'edit-name' })
    safe.field = 'name'
    return safe
  }
  return requestError(error, 'ARC could not create your account. Check again before trying to create it again, or contact your ARC administrator if this continues.')
}

export async function createGoogleAccount (options, { name, acknowledgedMatches = null }) {
  const { identity, context, assertCurrent, baselineState, lookupClient } = options
  const existing = await lookupGoogleAccount(options)
  if (existing) return verifyGoogleAccount({ ...options, account: existing })
  assertRegistrationAllowed()
  requireFreshLogin(context, identity.googleId)
  const displayName = typeof name === 'string' ? name.trim() : ''
  if (displayName.length < 3 || displayName.length > 255) {
    const error = new AuthMigrationError('REGISTRATION_NAME_INVALID', 'Enter a name between 3 and 255 characters.', { action: 'edit-name' })
    error.field = 'name'
    throw error
  }
  const matches = await findLegacyMatches(options)
  if (matches.usernameMatch || (matches.candidates.length && acknowledgedMatches !== matches.fingerprint) ||
      (acknowledgedMatches !== null && acknowledgedMatches !== matches.fingerprint)) {
    const error = new AuthMigrationError('REGISTRATION_MATCHES_CHANGED', 'Review the possible existing accounts before continuing.', { action: 'review-matches' })
    error.matches = matches
    throw error
  }
  // A second tab may have completed registration while suggestions were checked.
  const concurrent = await lookupGoogleAccount(options)
  if (concurrent) return verifyGoogleAccount({ ...options, account: concurrent })
  assertCurrent()
  let failure
  try {
    const { data } = await lookupClient.post('users', { name: displayName, email: identity.email, google_id: identity.googleId })
    assertCurrent()
    if (data && typeof data === 'object') {
      const expected = { google_id: identity.googleId, email: identity.email, ms_graph_id: null }
      for (const field of Object.keys(expected)) {
        if (Object.prototype.hasOwnProperty.call(data, field) && data[field] !== expected[field]) throw accountConflict()
      }
      const baseline = {}
      for (const field of ['id', 'api_key', 'ms_graph_id', 'google_id', 'email']) {
        if (Object.prototype.hasOwnProperty.call(data, field)) baseline[field] = data[field]
      }
      baselineState.creation = baseline
    }
  } catch (error) {
    assertCurrent()
    if (error instanceof AuthMigrationError) throw error
    failure = error
  }
  // Reconcile success, validation rejection, partial responses and lost responses
  // identically. A failed read never leads to an automatic second POST.
  const confirmed = await lookupGoogleAccount(options)
  if (confirmed) return verifyGoogleAccount({ ...options, account: confirmed })
  if (failure) throw registrationFailure(failure)
  throw new AuthMigrationError('REGISTRATION_UNCONFIRMED', 'ARC has not confirmed your new account. Check again before trying to create it again.', { retryable: true, action: 'retry' })
}
